/**
 * One event per code the camera scanner decodes.
 *
 * The database records what was scanned; it cannot say which decoder produced
 * the hit. That is the open question here: the software digital-zoom pass is
 * rolled out per device, and the only way to tell whether it earns its CPU on
 * a given phone is to compare hits attributed to it against the full-frame
 * decoder running beside it, and to see how often it merely re-reads a code
 * the camera already caught (dedup drops those).
 *
 * The scanned code never leaves the device: it identifies a product and, for
 * lot stickers, a specific batch. Only the shape of the decode is reported.
 */
import { getAnalyticsActor, posthog } from './posthog'

const decoders = ['camera', 'software_zoom'] as const
const scanModes = ['one_shot', 'continuous'] as const

export type ScannerDecoder = (typeof decoders)[number]
export type ScannerScanMode = (typeof scanModes)[number]

export interface ScannerDecodeEvent {
    /** Which decode pass produced the text. */
    decoder: ScannerDecoder
    /** Whether the dialog closes on the first hit or keeps recording. */
    scanMode: ScannerScanMode
    /** Symbology family the dialog was opened for. */
    formats: 'barcode' | 'qr'
    /** True when the dedup gate dropped this sighting (no record followed). */
    deduped: boolean
    /** Whether the software-zoom pass was running for this scan. */
    softwareZoomEnabled: boolean
}

/**
 * A decode is a browser-side fact: it says a decoder read something, not that
 * a product was found or a note was written. Never let it interrupt a scan.
 */
export function captureScannerDecode(
    event: ScannerDecodeEvent,
    actorContext?: { actor: string; impersonating: boolean },
) {
    try {
        const actor = actorContext ?? getAnalyticsActor()
        if (!actor) return
        if (!decoders.includes(event.decoder)) return
        if (!scanModes.includes(event.scanMode)) return
        posthog.capture('scanner_decode', {
            schema_version: 1,
            event_id: crypto.randomUUID(),
            ...actor,
            ...(actorContext ? { distinct_id: actor.actor } : {}),
            decoder: event.decoder,
            scan_mode: event.scanMode,
            formats: event.formats,
            deduped: event.deduped,
            software_zoom_enabled: event.softwareZoomEnabled,
        })
    } catch {
        // Analytics must never interrupt scanning or recording.
    }
}
